import React from "react";
import { CardContent } from "@/components/ui/card";
import type { BaseEntity, SimpleTableConfig, FieldValue, EntityWithRelations } from "../../types";
import type { EditingState } from "../types";
import { cardFieldCellClass } from "../utils/card-field-layout";
import { EditableField } from "./EditableField";
import { TagsDisplay } from "./TagsDisplay";

interface CardContentSectionProps<TData extends BaseEntity> {
  item: TData;
  contentFields: string[];
  config: SimpleTableConfig<TData>;
  editingCell: EditingState | null;
  editValue: string;
  onEditChange: (value: string) => void;
  onEdit: (rowId: string, fieldId: string, currentValue: FieldValue) => void;
  onSave: (rowId: string, fieldId: string) => void;
  onCancel: () => void;
  renderValue: (item: TData, fieldId: string) => React.ReactNode;
  inlineEditingDisabled?: boolean;
}

export function CardContentSection<TData extends BaseEntity>({
  item,
  contentFields,
  config,
  editingCell,
  editValue,
  onEditChange,
  onEdit,
  onSave,
  onCancel,
  renderValue,
  inlineEditingDisabled = false,
}: CardContentSectionProps<TData>) {
  if (contentFields.length === 0) {
    return null;
  }

  // Check if item has tags and if we should display them
  const itemTags = (item as EntityWithRelations<TData>).tags as string[] | undefined;
  const shouldShowTags = itemTags && itemTags.length > 0;

  return (
    <CardContent className="px-4 pt-0 pb-3 text-sm">
      <div className="pt-3 border-t border-border/40">
        <div className="grid grid-cols-3 gap-x-3 gap-y-2.5">
          {contentFields.map((fieldId) => {
            const fieldConfig = config.fields[fieldId];
            if (!fieldConfig) return null;

            if (fieldId === "tags") return null;

            return (
              <div
                key={fieldId}
                className={cardFieldCellClass(fieldConfig.cardSpan, "space-y-0.5")}
              >
                <div className="text-[0.65rem] font-semibold uppercase tracking-wider text-muted-foreground/60">
                  {fieldConfig.label}
                </div>
                <EditableField
                  item={item}
                  fieldId={fieldId}
                  editingCell={editingCell}
                  editValue={editValue}
                  onEditChange={onEditChange}
                  onEdit={onEdit}
                  onSave={onSave}
                  onCancel={onCancel}
                  renderValue={renderValue}
                  onClick={inlineEditingDisabled ? undefined : (e) => e.stopPropagation()}
                  readOnly={inlineEditingDisabled}
                  className="text-[0.8125rem] font-medium text-foreground"
                />
              </div>
            );
          })}
        </div>

        {shouldShowTags && (
          <div className="mt-2.5">
            <TagsDisplay tags={itemTags} maxTags={4} className="flex-wrap" />
          </div>
        )}
      </div>
    </CardContent>
  );
}
